"use client";

import { useState, type ReactNode } from "react";
import type { OptionalCategory } from "@/lib/consent";
import { allowed, openConsentSettings, saveConsent, useConsent } from "./store";

/** Öffnet die Cookie-Einstellungen, z. B. im Footer. */
export function ConsentSettingsButton({ className }: { className?: string }) {
  return (
    <button type="button" className={className} onClick={openConsentSettings}>
      Cookie-Einstellungen
    </button>
  );
}

/** Zeigt Kinder (Skripte, Widgets) erst nach Einwilligung in die Kategorie. */
export function ConsentGate({ category, children, fallback = null }: { category: OptionalCategory; children: ReactNode; fallback?: ReactNode }) {
  return allowed(useConsent(), category) ? children : fallback;
}

type VideoProps = { provider: "YouTube" | "Vimeo"; id: string; url: string; title: string };

/** Zwei-Klick-Lösung: Platzhalter ohne Verbindung zum Anbieter, iframe erst nach Klick oder Einwilligung. */
export function VideoEmbed({ provider, id, url, title }: VideoProps) {
  const consent = useConsent();
  const [once, setOnce] = useState(false);
  const label = title || `Video auf ${provider}`;
  if (once || allowed(consent, "media")) {
    const src = provider === "YouTube"
      ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?rel=0${once ? "&autoplay=1" : ""}`
      : `https://player.vimeo.com/video/${encodeURIComponent(id)}?dnt=1${once ? "&autoplay=1" : ""}`;
    return (
      <div className="video-frame">
        <iframe
          src={src}
          title={label}
          loading="lazy"
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
    );
  }
  return (
    <div className="video-consent" data-provider={provider} data-video={id}>
      <p className="video-title">{label}</p>
      <p>Dieses Video wird von {provider} bereitgestellt. Es wird erst geladen, wenn du zustimmst; dabei werden Daten an {provider} übertragen.</p>
      <div className="video-actions">
        <button type="button" className="video-load" onClick={() => setOnce(true)}>Video laden</button>
        <button
          type="button"
          className="video-always"
          onClick={() => saveConsent({ statistics: allowed(consent, "statistics"), marketing: allowed(consent, "marketing"), media: true })}
        >
          Externe Medien immer erlauben
        </button>
      </div>
      <a href={url} rel="noopener noreferrer">Auf {provider} ansehen</a>
    </div>
  );
}
